import React, { useState, useRef, useEffect } from "react";

/**
 * CustomSelectInput - A reusable custom select input component with optional search functionality
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - The label for the select input
 * @param {string} props.name - The name attribute for the input
 * @param {any} props.value - The currently selected value
 * @param {Function} props.onChange - Callback function called when selection changes
 * @param {Array} props.options - Array of option objects with value, name, and optional tagimage properties
 * @param {string} props.className - Additional CSS classes for the wrapper div
 * @param {string} props.labelClass - Additional CSS classes for the label
 * @param {string} props.inputClass - Additional CSS classes for the input
 * @param {boolean} props.required - Whether the field is required (default: false)
 * @param {string} props.error - Error message to display
 * @param {boolean} props.showSearch - Whether to show the search input in the dropdown (default: false)
 * @param {boolean} props.multiple - Opt-in multi-select. `value` is then an array of
 *   selected values and onChange emits an array. The option whose value is "" acts as
 *   the "clear all" row. Off by default, so every existing caller keeps single-select.
 * @returns {JSX.Element} The CustomSelectInput component
 */
export default function CustomSelectInput({
  label,
  name,
  value,
  onChange,
  options = [],
  className = "",
  labelClass = "",
  inputClass = "",
  required = false,
  error = "",
  showSearch = false,
  placeholder = "",
  multiple = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchTerm(''); // Reset search when clicking outside
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Multi-select bookkeeping. Only consulted when `multiple` is on, so the
  // single-select path below stays exactly as it was.
  const selectedValues = multiple ? (Array.isArray(value) ? value : (value ? [value] : [])) : [];
  const isChecked = (optValue) => selectedValues.includes(optValue);

  const handleSelect = (option) => {
    if (multiple) {
      // The blank option is the "clear all" row; everything else toggles and
      // leaves the list open so several can be picked in one go.
      const next = option.value === ''
        ? []
        : (isChecked(option.value)
            ? selectedValues.filter((v) => v !== option.value)
            : [...selectedValues, option.value]);
      onChange({ target: { name, value: next } });
      if (option.value === '') setIsOpen(false);
      return;
    }
    onChange({ target: { name, value: option.value } });
    setIsOpen(false);
    setSearchTerm(''); // Reset the search term when an option is selected
  };

  const selectedOption = multiple
    ? (selectedValues.length === 1
        ? options.find((opt) => opt.value === selectedValues[0])
        : (selectedValues.length > 1 ? { name: `${selectedValues.length} selected` } : undefined))
    : options.find((opt) => opt.value === value);

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      {label && (
        <label htmlFor={name} className={`block text-sm font-medium ${labelClass}`}>
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}

      <div
        className={`w-full h-[44px] flex items-center justify-between border rounded-lg px-3 bg-white cursor-pointer transition-colors hover:border-gray-400 ${inputClass} ${error ? "border-red-500 ring-red-500" : "border-gray-300"}`}
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setSearchTerm(''); // Reset search term when opening dropdown
          }
        }}
      >
        {selectedOption && selectedOption.name ? (
          <div className="flex items-center space-x-2">
            {selectedOption.tagimage && (
              <img src={selectedOption.tagimage} alt="" className="w-6 h-4" />
            )}
            <span className="text-gray-700 text-sm">{selectedOption.name}</span>
          </div>
        ) : (
          <span className="text-gray-400 text-sm">{placeholder || label || 'Select'}</span>
        )}

        <svg
          className={`w-4 h-4 transform transition-transform duration-200 ${isOpen ? "rotate-180" : ""
            }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <ul className="absolute z-[100] mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {showSearch && (
            <li className="px-4 py-2 sticky top-0 bg-white border-b">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded"
                onClick={(e) => e.stopPropagation()}
              />
            </li>
          )}
          {(() => {
            const filteredOptions = options.filter((opt) =>
              opt.name.toLowerCase().includes(searchTerm.toLowerCase())
            );

            if (filteredOptions.length === 0) {
              return (
                <li className="px-4 py-2 text-gray-500 text-center text-sm">
                  No results found
                </li>
              );
            }

            return filteredOptions.map((opt) => (
              <li
                key={opt.value}
                onClick={() => handleSelect(opt)}
                className={`px-4 py-2 hover:bg-red-100 cursor-pointer flex items-center space-x-2 ${
                  multiple && opt.value !== '' && isChecked(opt.value) ? 'bg-red-50' : ''
                }`}
              >
                {multiple && opt.value !== '' && (
                  <input
                    type="checkbox"
                    readOnly
                    checked={isChecked(opt.value)}
                    className="w-4 h-4 rounded border-gray-300 accent-[#EE3B31] cursor-pointer pointer-events-none"
                  />
                )}
                {opt.tagimage && (
                  <img src={opt.tagimage} alt="" className="w-6 h-4" />
                )}
                <span className="text-sm">{opt.name}</span>
              </li>
            ));
          })()}
        </ul>
      )}

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
}
